import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import qmsDocumentTypeActions from "../../../_actions/_qmsActions/qmsDocumentTypeActions";
import { QmsDataEditModalContext } from "../_common/QmsDataTable";
import CustomDatePickerInput from "../../_common/CustomDatePickerInput";
import qmsDocumentActions from "../../../_actions/_qmsActions/qmsDocumentActions";
import qmsTeamActions from "../../../_actions/_qmsActions/qmsTeamActions";

export default function QmsAdminModal_EditDocument(props) {
  const { divisionId } = props;

  const qmsDataEditModal = useContext(QmsDataEditModalContext);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(qmsDocumentTypeActions.getAll(divisionId));
    dispatch(qmsTeamActions.getAll(divisionId));
  }, []);

  const docTypes = useSelector((state) => state.qms.qmsDocumentTypes[divisionId] || null);
  const qmsTeams = useSelector((state) => state.qms.qmsTeams[divisionId] || null);

  const fileExists = useRef(
    qmsDataEditModal.modalState.data &&
      "filePath" in qmsDataEditModal.modalState.data &&
      qmsDataEditModal.modalState.data.filePath
      ? true
      : false
  );

  const docTypeOptions = useMemo(
    () =>
      (docTypes || []).map((dt) => ({ label: dt.label, value: dt.qmsDocumentTypeId })),
    [docTypes]
  );

  const qmsTeamOptions = useMemo(
    () => (qmsTeams || []).map((qt) => ({ label: qt.name, value: qt.qmsTeamId })),
    [qmsTeams]
  );

  const [title, setTitle] = useState(
    qmsDataEditModal.modalState.data && "title" in qmsDataEditModal.modalState.data
      ? qmsDataEditModal.modalState.data.title
      : ""
  );
  const [number, setNumber] = useState(
    qmsDataEditModal.modalState.data && "number" in qmsDataEditModal.modalState.data
      ? qmsDataEditModal.modalState.data.number
      : ""
  );
  const [type, setType] = useState(null); // Initialize after docType options populate
  const [lastRevisedOn, setLastRevisedOn] = useState(
    qmsDataEditModal.modalState.data &&
      "lastRevisedOn" in qmsDataEditModal.modalState.data &&
      qmsDataEditModal.modalState.data.lastRevisedOn
      ? new Date(qmsDataEditModal.modalState.data.lastRevisedOn)
      : null
  );
  const [description, setDescription] = useState(
    qmsDataEditModal.modalState.data && "description" in qmsDataEditModal.modalState.data
      ? qmsDataEditModal.modalState.data.description
      : ""
  );
  const [owner, setOwner] = useState(null); // Initialize after qmsTeams is available
  const [file, setFile] = useState(null);
  const [showFileUpload, _setShowFileUpload] = useState(!fileExists.current);
  const toggleFileUpload = () => _setShowFileUpload(!showFileUpload);

  const [formErrors, _setFormErrors] = useState({
    title: null,
    number: null,
    type: null,
  });
  const setFormError = (fieldName, message) =>
    _setFormErrors({ ...formErrors, [fieldName]: message });
  const clearFormError = (fieldName) =>
    _setFormErrors({ ...formErrors, [fieldName]: null });

  useEffect(() => {
    if (
      docTypeOptions &&
      (docTypeOptions || []).length &&
      qmsDataEditModal.modalState.data &&
      "qmsDocumentTypeId" in qmsDataEditModal.modalState.data &&
      qmsDataEditModal.modalState.data.qmsDocumentTypeId
    ) {
      setType(
        docTypeOptions.find(
          (dto) => dto.value === qmsDataEditModal.modalState.data.qmsDocumentTypeId
        )
      );
    }
  }, [docTypeOptions]);

  useEffect(() => {
    if (
      qmsTeams &&
      qmsDataEditModal.modalState.data &&
      "ownerTeamId" in qmsDataEditModal.modalState.data &&
      qmsDataEditModal.modalState.data.ownerTeamId
    ) {
      const qto = (qmsTeamOptions || []).find(
        (o) => o.value === qmsDataEditModal.modalState.data.ownerTeamId
      );
      if (qto) setOwner(qto);
    }
  }, [qmsTeamOptions]);

  const validate = (fieldName) => {
    switch (fieldName) {
      case "title":
        if (!title) setFormError(fieldName, "Required");
        else clearFormError(fieldName);
        break;
      case "number":
        if (!number) setFormError(fieldName, "Required");
        else clearFormError(fieldName);
        break;
      case "type":
        if (!type) setFormError(fieldName, "Required");
        else clearFormError(fieldName);
        break;
      default:
        break;
    }
    return;
  };

  const formIsIncomplete = () =>
    !title || !number || !type || Object.values(formErrors).some((e) => e !== null);

  const handleChange_fileInput = (e) => setFile(e.target.files[0] || null);

  const handleClick_submit = async (e) => {
    e.preventDefault();
    const qmsDocData = {
      qmsDocumentId: qmsDataEditModal.modalState.data
        ? qmsDataEditModal.modalState.data.qmsDocumentId
        : 0,
      divisionId,
      title,
      number,
      qmsDocumentTypeId: type ? type.value : null,
      description: description || "",
      lastRevisedOn: lastRevisedOn ? lastRevisedOn.toISOString() : null,
      ownerTeamId: owner ? owner.value : null,
      // Keep the filePath to existing value if one exists, whether it was marked for deletion or not
      filePath: fileExists.current ? qmsDataEditModal.modalState.data.filePath : "",
      // Delete existing if 1) there is an existing file, and 2) it was requested for deletion
      // (#2 is true if "Delete" button was clicked and file upload input was toggled on.)
      deleteExistingFile: fileExists.current && showFileUpload,
    };

    let formData = new FormData();
    formData.append("qmsDocumentFile", file);
    Object.entries(qmsDocData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v, i) => {
          if (v !== null && typeof v === "object" && !Array.isArray(v)) {
            Object.entries(v).forEach(([oKey, oValue]) => {
              formData.append(`${key}[${i}].${oKey}`, oValue);
            });
          } else formData.append(`${key}[${i}]`, v);
        });
      } else formData.append(key, value);
    });

    if (qmsDataEditModal.modalState.data)
      await dispatch(qmsDocumentActions.update(formData, divisionId));
    else await dispatch(qmsDocumentActions.add(formData, divisionId));
    qmsDataEditModal.toggleModal();
  };

  const closeBtn = (
    <button className="close" onClick={qmsDataEditModal.toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <form
      id="editQmsDocumentForm"
      onSubmit={handleClick_submit}
      autoComplete="off"
      encType="multipart/form-data"
    >
      <Modal
        isOpen={qmsDataEditModal.modalState.showModal}
        toggle={qmsDataEditModal.toggleModal}
        fade={false}
        size={"lg"}
      >
        <ModalHeader
          toggle={qmsDataEditModal.toggleModal}
          className="bg-info text-white"
          close={closeBtn}
        >
          <FontAwesomeIcon
            icon={qmsDataEditModal.modalState.data ? "edit" : "plus"}
            className="fa-fw mr-2"
          />
          {qmsDataEditModal.modalState.data ? `Edit Document Info` : `Add New Document`}
        </ModalHeader>
        <ModalBody>
          <div className="form-row mb-2">
            <div className="form-group col-md-3 mb-0 pr-1">
              <label htmlFor="label" className="mb-0">
                <b>Document No.:</b>*
              </label>
              <input
                type="text"
                name="number"
                id="number"
                className="form-control"
                maxLength="15"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                onBlur={(e) => validate(e.target.name)}
              />
              {formErrors.number && (
                <span className="error-small">
                  <small>{formErrors.number}</small>
                </span>
              )}
            </div>
            <div className="form-group col-md-9 mb-0 pl-1">
              <label htmlFor="title" className="mb-0">
                <b>Title:</b>*
              </label>
              <input
                type="text"
                name="title"
                id="title"
                className="form-control"
                maxLength="150"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={(e) => validate(e.target.name)}
              />
              {formErrors.title && (
                <span className="error-small">
                  <small>{formErrors.title}</small>
                </span>
              )}
            </div>
          </div>
          <div className="form-row mb-2">
            <div className="form-group col-md-6 mb-0 pr-1">
              <label htmlFor="type" className="mb-0">
                <b>Document Type:</b>*
              </label>
              <Select
                id="type"
                name="type"
                value={type}
                options={docTypeOptions}
                onChange={(selected) => setType(selected)}
                isClearable
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>
            <div className="form-group col-md-6 mb-0 pr-1">
              <label htmlFor="owner" className="mb-0">
                Document Owner:
              </label>
              <Select
                id="owner"
                name="owner"
                value={owner}
                options={qmsTeamOptions}
                onChange={(selected) => setOwner(selected)}
                isClearable
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>
          </div>
          <div className="form-row mb-2">
            <div className="form-group col-md-12 mb-0">
              <label htmlFor="make" className="mb-0">
                Description:
              </label>
              <textarea
                name="description"
                id="description"
                className="form-control"
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={(e) => validate(e.target.name)}
              />
            </div>
          </div>
          <div className="form-row mb-0">
            <div className="form-group col-md-7 mb-0 pr-1">
              <label htmlFor="file" className="mb-0">
                File:
              </label>
              <div className="col-12 ">
                {fileExists.current && (
                  <div className="row">
                    <div className="col-12 px-1">
                      <div className="form-row d-flex flex-row">
                        <div className="flex-grow-1 pl-0 mr-1">
                          <button
                            id="existFile"
                            name="existFile"
                            className={`btn btn-link border text-left pl-1 w-100${
                              showFileUpload ? " text-red" : ""
                            }`}
                            style={{
                              textOverflow: "ellipsis",
                              textDecoration: showFileUpload ? "line-through" : "",
                            }}
                            onClick={() =>
                              dispatch(
                                qmsDocumentActions.downloadFile(
                                  qmsDataEditModal.modalState.data.qmsDocumentId,
                                  qmsDataEditModal.modalState.data.originalFilename ||
                                    "file"
                                )
                              )
                            }
                          >
                            {qmsDataEditModal.modalState.data.originalFilename ||
                              "(Filename N/A)"}
                          </button>
                        </div>
                        <button
                          id="toggleDeleteFile"
                          name="toggleDeleteFile"
                          className={
                            showFileUpload ? "btn btn-secondary" : "btn btn-danger"
                          }
                          onClick={() => toggleFileUpload()}
                        >
                          <FontAwesomeIcon
                            icon={showFileUpload ? "trash-restore" : "trash-alt"}
                            className="fa-fw mr-1"
                          />
                          {showFileUpload ? "Undo Delete" : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {showFileUpload && (
                  <div className={`row${fileExists.current ? " mt-2" : ""}`}>
                    <div className="col-12 custom-file">
                      <input
                        type="file"
                        id="file"
                        name="file"
                        className="custom-file-input"
                        onChange={handleChange_fileInput}
                        accept=".docx,.xlsx,.pdf"
                      />
                      <label htmlFor="file" className="custom-file-label">
                        {file ? file.name : "Click/tap to choose file..."}
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group col-md-5 mb-0 pl-1">
              <label htmlFor="lastRevisedOn" className="mb-0">
                Published/Revised On:
              </label>
              <div className="ml-auto">
                <DatePicker
                  customInput={<CustomDatePickerInput />}
                  name="lastRevisedOn"
                  id="lastRevisedOn"
                  selected={lastRevisedOn}
                  dateFormat="MM/dd/yyyy (E)"
                  onChange={(date) => setLastRevisedOn(date)}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0">
            <input
              id="submit"
              name="submit"
              type="submit"
              className="btn btn-primary font-weight-bold"
              form="editQmsDocumentForm"
              onClick={handleClick_submit}
              value={qmsDataEditModal.modalState.data ? "Update" : "Add"}
              disabled={formIsIncomplete()}
            />
            <button
              className="btn btn-secondary ml-2"
              color="secondary"
              onClick={qmsDataEditModal.toggleModal}
            >
              Cancel
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </form>
  );
}
